import { isNullOrUndefined, logDebug, getDUName } from '../js/Common';

import { useRef } from 'react';
import { useCustomCompareEffect } from '../js/UseCustomCompareEffect';
import vtkMapper from "vtk.js/Sources/Rendering/Core/Mapper";
import vtkActor from "vtk.js/Sources/Rendering/Core/Actor";
import { equals } from "fable-compiler/bin/fable-library/Util.js";
import { Union } from "fable-compiler/bin/fable-library/Types.js";

const isDebugging = false;

// Corresponds to the F# PlaneIdentifier DU.
const PlaneIdentifier = Object.freeze({
    First: "First",
    Second: "Second",
    Third: "Third"
});

function log(msg) {
    logDebug("MeshVisualization.CenterlineMeasurement", isDebugging, msg);
}

function planePipelinesEqual(a, b) {
    return Array.isArray(a) &&
        Array.isArray(b) &&
        a.length === b.length &&
        a.every((val, index) => equals(val.centerlinePlane, b[index].centerlinePlane));
}

function compareDependencies(a, b) {
    return Array.isArray(a) &&
        Array.isArray(b) &&
        a.length === b.length &&
        (
            a.length < 2 || // number of dependencies
            planePipelinesEqual(a[0], b[0]) && // planePipelines dependency
            a[1] === b[1] // selectedPlaneIdentifier dependency
        );
}

/**
 * @param container
 * @param parentContext
 * @param initialized
 * @param {{centerlinePlane: {centerlineType: Union, branchSelection: Union, index: num, callbackIdentifier: string, planeIdentifier: Union}, centerlinePlaneSource: {rootFilter: any, planeIndexFilter: any}}[]} planePipelines
 */
export function useCenterlineMeasurement(
    container,
    parentContext,
    { planePipelines, selectedPlaneIdentifier }
) {
    const context = useRef(null);

    function removePreviousActors(previousActors, renderer) {
        for (let i = 0; i < previousActors.length; i++) {
            let actor = previousActors[i].actor;
            let mapper = previousActors[i].mapper;

            if (renderer) {
                renderer.removeActor(actor);
            }
            actor.delete();
            mapper.delete();
        }
    }

    useCustomCompareEffect(() => {
        if (container && parentContext) {

            if (!context.current) {
                // Initialize
                log("Initializing centerline measurement");

                let previousActors = [];

                context.current =
                    { previousActors };
            }

            const { renderer, renderWindow, istyle } = parentContext;
            const { previousActors } = context.current;

            istyle.setIsMouseWheelScrollEnabled(isNullOrUndefined(selectedPlaneIdentifier));

            let updatedPreviousActors = [];

            if (planePipelines) {
                removePreviousActors(previousActors, renderer);

                for (let i = 0; i < planePipelines.length; i++) {
                    let source = planePipelines[i].centerlinePlaneSource.planeIndexFilter;

                    let port = source.getOutputPort();
                    let planeMapper = vtkMapper.newInstance();
                    let planeActor = vtkActor.newInstance();

                    planeMapper.setInputConnection(port);
                    planeActor.setMapper(planeMapper);

                    planeActor.getProperty().setEdgeVisibility(true);
                    planeActor.getProperty().setOpacity(0.6);
                    planeActor.getProperty().setLighting(false);

                    if (getDUName(planePipelines[i].centerlinePlane.planeIdentifier) === PlaneIdentifier.First) {
                        planeActor.getProperty().setColor(0, 0, 1);
                        planeActor.getProperty().setEdgeColor(0.5, 0.5, 1);
                    }
                    else if (getDUName(planePipelines[i].centerlinePlane.planeIdentifier) === PlaneIdentifier.Second) {
                        planeActor.getProperty().setColor(1, 0, 0);
                        planeActor.getProperty().setEdgeColor(1, 0.5, 0.5);
                    }
                    else {
                        planeActor.getProperty().setColor(0, 1, 0);
                        planeActor.getProperty().setEdgeColor(0.5, 1, 0.5);
                    }

                    updatedPreviousActors.push({ actor: planeActor, mapper: planeMapper });
                    renderer.addActor(planeActor);
                }

                renderer.resetCameraClippingRange();
                renderWindow.render();
            }

            context.current.previousActors = updatedPreviousActors;
        }

        const ctx = context.current;

        return () => {
            // Dispose

            if (ctx) {
                const { previousActors } = ctx;

                let renderer;
                if (container && parentContext) {
                    renderer = parentContext.renderer;
                }

                removePreviousActors(previousActors, renderer);

            }
        };
    }, [planePipelines, selectedPlaneIdentifier], compareDependencies);
}